'use client'

import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { useTranslation } from 'react-i18next'
import type { StorageLevel } from '@garage/shared'
import { Dialog, Button, PrintIcon, Spinner } from '@/ui'

interface LevelQrDialogProps {
  level: StorageLevel | null
  onClose: () => void
}

/**
 * A QR code that opens this level's own page directly — scan it (a phone on
 * the same network as the workshop PC) and it lands straight on what's
 * allocated here, no navigating the rack/level tiles first. Meant to be
 * printed and stuck on the physical shelf.
 */
export function LevelQrDialog({ level, onClose }: LevelQrDialogProps) {
  const { t } = useTranslation()
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const url = level ? `${window.location.origin}/storage/levels/${level.id}` : ''

  useEffect(() => {
    if (!level) {
      setDataUrl(null)
      return
    }
    let cancelled = false
    setDataUrl(null)
    QRCode.toDataURL(url, { width: 320, margin: 2 }).then((generated) => {
      if (!cancelled) setDataUrl(generated)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level?.id])

  if (!level) return null

  return (
    <Dialog
      open={!!level}
      onClose={onClose}
      title={t('storage.qrCodeTitle', { level: level.name })}
      description={t('storage.qrCodeDescription')}
      size="sm"
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="filled" icon={<PrintIcon className="w-4 h-4" />} onClick={() => window.print()}>
            {t('common.print')}
          </Button>
        </div>
      }
    >
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #qr-print-area, #qr-print-area * { visibility: visible; }
          #qr-print-area { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; }
        }
      `}</style>
      <div id="qr-print-area" className="flex flex-col items-center gap-3 py-2">
        {dataUrl ? (
          <img src={dataUrl} alt={t('storage.qrCodeAlt', { level: level.name })} className="w-64 h-64" />
        ) : (
          <div className="w-64 h-64 flex items-center justify-center">
            <Spinner size="md" />
          </div>
        )}
        <div className="text-title-md text-on-surface">{level.name}</div>
        <div className="text-body-sm text-on-surface-variant break-all text-center">{url}</div>
      </div>
    </Dialog>
  )
}
